import type { LanguageCode } from "./language";

/**
 * Widget interface strings.
 *
 * English is split by region because spelling differs in ways customers
 * notice "personalise" vs "personalize" and a UK site showing American
 * spelling reads as a foreign product.
 */
export interface Strings {
  openChat: string;
  minimise: string;
  sendMessage: string;
  typeMessage: string;
  genericError: string;
  helpful: string;
  notHelpful: string;
  sources: string;
  source: string;
  talkToHuman: string;
  handoffPrompt: string;
  emailPlaceholder: string;
  emailLabel: string;
  detailsPlaceholder: string;
  detailsLabel: string;
  send: string;
  cancel: string;
  handoffError: string;
  sendFirst: string;
  preChatName: string;
  preChatEmail: string;
  preChatStart: string;
  preChatSkip: string;
  dismissGreeting: string;
  /**
   * Shown in place of the assistant when the embed itself is wrong. Written
   * for whoever installed the widget, not the visitor: they are the only
   * person who can fix it, and a vague "something went wrong" sends them to
   * support instead of to the dashboard.
   */
  invalidSiteKey: string;
  originNotAllowed: string;
  /** The API could not be reached at all: wrong data-api-url, server down, or blocked. */
  apiUnreachable: (apiUrl: string) => string;
}

const EN_GB: Strings = {
  openChat: "Open chat",
  minimise: "Minimise chat",
  sendMessage: "Send message",
  typeMessage: "Type a message…",
  genericError: "Sorry, something went wrong. Please try again.",
  helpful: "Helpful",
  notHelpful: "Not helpful",
  sources: "Sources",
  source: "Source",
  talkToHuman: "Talk to a human",
  handoffPrompt: "Leave your email and our team will get back to you.",
  emailPlaceholder: "jhon@minaya.ai",
  emailLabel: "Your email address",
  detailsPlaceholder: "Anything else we should know? (optional)",
  detailsLabel: "Additional details",
  send: "Send",
  cancel: "Cancel",
  handoffError: "Sorry, that did not go through. Please try again in a moment.",
  sendFirst: "Please send a message first.",
  preChatName: "Your name",
  preChatEmail: "Your email",
  preChatStart: "Start chat",
  preChatSkip: "Skip",
  dismissGreeting: "Dismiss",
  invalidSiteKey:
    "This chat isn't configured correctly: the site key in the embed code doesn't match any Minaya widget. " +
    "Open your Minaya dashboard, go to Embed, copy the site key again and update the script on this page.",
  originNotAllowed:
    "This chat isn't configured correctly: this website isn't in the widget's allowed origins. " +
    "Open your Minaya dashboard, go to Widget settings, and add this domain to the allowed origins.",
  apiUnreachable: (apiUrl) =>
    `This chat can't reach Minaya at ${apiUrl}. Check that the API URL in the embed code is correct and that the server is running, then reload the page.`,
};

export const STRINGS: Record<LanguageCode, Strings> = {
  "en-GB": EN_GB,

  // Differences from en-GB are spelling and register only.
  "en-US": {
    ...EN_GB,
    minimise: "Minimize chat",
    handoffError:
      "Sorry, that didn't go through. Please try again in a moment.",
  },

  "en-AU": {
    ...EN_GB,
    handoffPrompt: "Leave your email and our team will get back to you.",
  },

  de: {
    openChat: "Chat öffnen",
    minimise: "Chat minimieren",
    sendMessage: "Nachricht senden",
    typeMessage: "Nachricht schreiben …",
    genericError:
      "Entschuldigung, da ist etwas schiefgelaufen. Bitte versuchen Sie es erneut.",
    helpful: "Hilfreich",
    notHelpful: "Nicht hilfreich",
    sources: "Quellen",
    source: "Quelle",
    talkToHuman: "Mit einem Menschen sprechen",
    handoffPrompt:
      "Hinterlassen Sie Ihre E-Mail-Adresse, unser Team meldet sich bei Ihnen.",
    emailPlaceholder: "name@beispiel.de",
    emailLabel: "Ihre E-Mail-Adresse",
    detailsPlaceholder: "Möchten Sie uns noch etwas mitteilen? (optional)",
    detailsLabel: "Weitere Angaben",
    send: "Senden",
    cancel: "Abbrechen",
    handoffError:
      "Das hat leider nicht geklappt. Bitte versuchen Sie es gleich noch einmal.",
    sendFirst: "Bitte senden Sie zuerst eine Nachricht.",
    preChatName: "Ihr Name",
    preChatEmail: "Ihre E-Mail-Adresse",
    preChatStart: "Chat starten",
    preChatSkip: "Überspringen",
    dismissGreeting: "Schließen",
    invalidSiteKey:
      "Dieser Chat ist nicht richtig konfiguriert: Der Site-Key im Einbettungscode passt zu keinem Minaya-Widget. " +
      "Öffnen Sie Ihr Minaya-Dashboard, gehen Sie zu „Einbetten“, kopieren Sie den Site-Key erneut und aktualisieren Sie das Skript auf dieser Seite.",
    originNotAllowed:
      "Dieser Chat ist nicht richtig konfiguriert: Diese Website steht nicht in den erlaubten Ursprüngen des Widgets. " +
      "Öffnen Sie Ihr Minaya-Dashboard, gehen Sie zu den Widget-Einstellungen und fügen Sie diese Domain hinzu.",
    apiUnreachable: (apiUrl) =>
      `Dieser Chat erreicht Minaya unter ${apiUrl} nicht. Prüfen Sie, ob die API-URL im Einbettungscode stimmt und der Server läuft, und laden Sie die Seite dann neu.`,
  },

  ar: {
    openChat: "فتح المحادثة",
    minimise: "تصغير المحادثة",
    sendMessage: "إرسال الرسالة",
    typeMessage: "اكتب رسالة…",
    genericError: "عذرًا، حدث خطأ ما. يرجى المحاولة مرة أخرى.",
    helpful: "مفيد",
    notHelpful: "غير مفيد",
    sources: "المصادر",
    source: "المصدر",
    talkToHuman: "التحدث إلى موظف",
    handoffPrompt: "اترك بريدك الإلكتروني وسيتواصل معك فريقنا.",
    emailPlaceholder: "jhon@minaya.ai",
    emailLabel: "بريدك الإلكتروني",
    detailsPlaceholder: "هل هناك ما تود إخبارنا به؟ (اختياري)",
    detailsLabel: "تفاصيل إضافية",
    send: "إرسال",
    cancel: "إلغاء",
    handoffError: "لم يتم الإرسال. يرجى المحاولة بعد قليل.",
    sendFirst: "يرجى إرسال رسالة أولاً.",
    preChatName: "اسمك",
    preChatEmail: "بريدك الإلكتروني",
    preChatStart: "ابدأ المحادثة",
    preChatSkip: "تخطٍ",
    dismissGreeting: "إغلاق",
    invalidSiteKey:
      "لم يتم إعداد هذه الدردشة بشكل صحيح: مفتاح الموقع في كود التضمين لا يطابق أي أداة Minaya. " +
      "افتح لوحة تحكم Minaya، ثم انتقل إلى «التضمين»، وانسخ مفتاح الموقع مرة أخرى وحدّث السكربت في هذه الصفحة.",
    originNotAllowed:
      "لم يتم إعداد هذه الدردشة بشكل صحيح: هذا الموقع غير مدرج ضمن المصادر المسموح بها للأداة. " +
      "افتح لوحة تحكم Minaya، ثم انتقل إلى إعدادات الأداة وأضف هذا النطاق إلى المصادر المسموح بها.",
    apiUnreachable: (apiUrl) =>
      `تعذّر على هذه الدردشة الوصول إلى Minaya على ${apiUrl}. تأكد من صحة عنوان API في كود التضمين ومن أن الخادم يعمل، ثم أعد تحميل الصفحة.`,
  },
  zh: {
    openChat: "打开对话",
    minimise: "最小化对话",
    sendMessage: "发送消息",
    typeMessage: "输入消息…",
    genericError: "抱歉，出了点问题。请重试。",
    helpful: "有帮助",
    notHelpful: "没帮助",
    sources: "来源",
    source: "来源",
    talkToHuman: "联系人工客服",
    handoffPrompt: "留下您的邮箱，我们的团队会尽快与您联系。",
    emailPlaceholder: "jhon@minaya.ai",
    emailLabel: "您的邮箱地址",
    detailsPlaceholder: "还有什么需要告诉我们的吗？（选填）",
    detailsLabel: "补充信息",
    send: "发送",
    cancel: "取消",
    handoffError: "抱歉，发送失败。请稍后重试。",
    sendFirst: "请先发送一条消息。",
    preChatName: "您的姓名",
    preChatEmail: "您的邮箱",
    preChatStart: "开始对话",
    preChatSkip: "跳过",
    dismissGreeting: "关闭",
    invalidSiteKey:
      "此聊天窗口配置有误：嵌入代码中的站点密钥与任何 Minaya 小部件都不匹配。" +
      "请打开 Minaya 控制台，进入“嵌入”页面，重新复制站点密钥并更新本页面上的脚本。",
    originNotAllowed:
      "此聊天窗口配置有误：本网站不在该小部件的允许来源列表中。" +
      "请打开 Minaya 控制台，进入小部件设置，将此域名添加到允许来源。",
    apiUnreachable: (apiUrl) =>
      `此聊天窗口无法连接到 ${apiUrl} 上的 Minaya。请检查嵌入代码中的 API 地址是否正确、服务器是否正在运行，然后重新加载页面。`,
  },
};

export function stringsFor(language: LanguageCode): Strings {
  return STRINGS[language] ?? STRINGS["en-GB"];
}

/** Full names, used when instructing the model which language to reply in. */
export const LANGUAGE_NAMES: Record<LanguageCode, string> = {
  "en-GB": "English (United Kingdom)",
  "en-US": "English (United States)",
  "en-AU": "English (Australia)",
  de: "German",
  ar: "Arabic",
  zh: "Chinese (Simplified)",
};
